"use client";

import type { ReactNode } from "react";

import { cn } from "@homeinstead/ui";
import { useEffect, useRef, useSyncExternalStore } from "react";
import { usePrefersReducedMotion } from "~www/hooks/use-prefers-reduced-motion";

type SectionBackgroundVideoProps = {
  className?: string;
  poster?: ReactNode;
  src: string;
};

function subscribeToNothing() {
  return () => {};
}

/**
 * The server cannot know the visitor's motion preference, so markup rendered
 * before hydration keeps the video hidden and unloaded; otherwise
 * reduced-motion visitors would download and briefly play it.
 */
function useHasHydrated() {
  return useSyncExternalStore(
    subscribeToNothing,
    () => true,
    () => false,
  );
}

/**
 * Muted, looping background video over its poster image. Playback starts from
 * an effect rather than `autoPlay` so a reduced-motion preference keeps the
 * poster on screen.
 */
function SectionBackgroundVideo({
  className,
  poster,
  src,
}: SectionBackgroundVideoProps) {
  const hasHydrated = useHasHydrated();
  const prefersReducedMotion = usePrefersReducedMotion();
  const videoRef = useRef<HTMLVideoElement>(null);
  const isPlaying = hasHydrated && !prefersReducedMotion;

  useEffect(() => {
    const video = videoRef.current;

    if (!video) {
      return;
    }

    if (isPlaying) {
      video.play()?.catch(() => {});
    } else {
      video.pause();
    }
  }, [isPlaying]);

  return (
    <>
      {poster}
      <video
        aria-hidden
        className={cn("absolute inset-0 size-full", className, {
          invisible: !isPlaying,
        })}
        loop
        muted
        playsInline
        preload={isPlaying ? "auto" : "none"}
        ref={videoRef}
        src={src}
      />
    </>
  );
}

export { SectionBackgroundVideo };
